import { TheWestSection } from '@news-mono/common'
import {
    AllEvents,
    PublicationType,
    useFeatureValue,
} from '@news-mono/web-common'
import { RequiredAccessLevel } from '@west-australian-newspapers/publication-types'
import { Logger } from '@west-australian-newspapers/typescript-log'
import debug from 'debug'
import H from 'history'
import React, { useMemo } from 'react'
import { useSection } from '../../../../__helpers'
import { useFeatureForRegionalSections } from '../../../../feature-toggling'
import {
    SubscriptionPackages,
    rrmePackageOfferDetails,
    useUpdateBreachWallContext,
} from '../../common'
import { getBreachWallOptions } from '../../helpers'
import { BreachWallContentType } from '../../helpers/BreachWallTypes'
import BreachWallContent from '../BreachWallContent/BreachWallContent'
import {
    StyledTitle,
    StyledTitleImage,
} from '../BreachWallContent/BreachWallContent.styled'
import { BreachWallTerms } from '../BreachWallTerms/BreachWallTerms'
import { BreachScreenRegistrationProps } from '../../breachscreen.routing'

const breachWallDebug = debug('breach-wall')

export const defaultCreative = 'TW_Article_Breach_WestBlue_Design_PhotoPackages'
export const DEFAULT_BREACH_TYPE: BreachWallContentType =
    'digital-intro-default'

export type BreachWallOptions = {
    canShowSportBreach: boolean
    canShowBreachDiscountFootyLivesHere: boolean
    canShowBreachDiscount50sch: boolean
    canShowBlackFriday: boolean
    canShowBreachDiscount50Off: boolean
    canShowBusinessPackDiscount: boolean
    canShowSportPackDiscount: boolean
    canShowAnnualPackage: boolean
    canShowSummerOfCricketBreach: boolean
}

export type BreachWallContentWrapperProps = BreachScreenRegistrationProps & {
    // Publication data to update breach wall context
    publication?: PublicationType
    requiredAccess?: RequiredAccessLevel
    location: H.Location
    // Consume all events
    onEvent: (event: AllEvents) => void
    log: Logger
    isTest?: boolean
}

export const BreachWallContentWrapper: React.FC<
    BreachWallContentWrapperProps
> = ({
    breachType: suppliedType,
    breachCreativeName: breachCreative = defaultCreative,
    publication,
    requiredAccess,
    onEvent,
    log,
    isTest,
    location,
}) => {
    const section = useSection<TheWestSection>()
    const isRegionalOverride = useFeatureForRegionalSections(
        'breach-regional-override',
        section,
    )
    // Get the current breach campaign based on the feature toggle, falls back to default breach type
    const currentBreachWallType =
        useFeatureValue<BreachWallContentType>(
            'campaign-offer',
            DEFAULT_BREACH_TYPE,
        ) ?? DEFAULT_BREACH_TYPE

    // Store breach display values
    const { deal: breachDeal, type: breachType } = useMemo(() => {
        // Uses the suppliedType first, if provided, otherwise uses the toggle
        const breachWallType: BreachWallContentType =
            suppliedType ?? currentBreachWallType
        return getBreachWallOptions(location, breachWallType)
    }, [location, suppliedType, currentBreachWallType])

    // fire breach wall context update
    useUpdateBreachWallContext({
        publication: publication,
        requiredAccessLevel: requiredAccess || 'subscriber',
        breachDeal: breachDeal,
        breachCreativeName: breachCreative,
    })

    const subscriptionPackages: SubscriptionPackages = useMemo(() => {
        return rrmePackageOfferDetails(breachType, log)
    }, [breachType, log])

    // Debug the subscriptionPackages being pulled through
    breachWallDebug(
        {
            subscriptionPackages,
        },
        `BreachWallContentWrapper rendered the ${breachType} package with values:`,
    )

    return (
        <div data-testid="BreachScreen">
            <BreachWallContent
                breachCreativeName={breachCreative}
                breachType={breachType}
                breachDeal={breachDeal}
                isTest={isTest}
                onEvent={onEvent}
                title={() => {
                    let img

                    switch (breachType) {
                        case 'afl-footy-lives-here-breach-screen':
                            img = require(`../../assets/WA_footy_lives_here_logo.png`)
                            return (
                                <StyledTitleImage
                                    breachType={breachType}
                                    src={img}
                                    alt={'WA Footy Lives Here Banner'}
                                />
                            )
                        case '50-off-campaign':
                            img = require('../../assets/50-off-campaign-title-image.png')
                            return (
                                <StyledTitleImage
                                    src={img}
                                    breachType={breachType}
                                    alt={'50% Off New Subscriptions'}
                                />
                            )
                        default:
                            return (
                                <StyledTitle breachType={breachType}>
                                    The No. 1 source of news for every West
                                    Australian
                                </StyledTitle>
                            )
                    }
                }}
                footer={() => null}
                terms={() => <BreachWallTerms breachType={breachType} />}
                //Media banner appears above the title
                mediaBanner={() => null}
                //Breach banner: "Subscribers with digital access can view this article."
                breachBanner={() => null}
                subscriptionPackages={subscriptionPackages}
                isRegionalOverride={isRegionalOverride}
            />
        </div>
    )
}

BreachWallContentWrapper.displayName = 'BreachWallContentWrapper'

export default BreachWallContentWrapper
